import { Component, DestroyRef, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { DataShareService } from "../services/data-share.service";
import { CountryPageIndicatorsService } from "../services/country-page-indicators.service";
import {
  CatalogueUiReusableComponentsModule
} from 'src/survey-tool/catalogue-ui/shared/reusable-components/catalogue-ui-reusable-components.module';
import {
  SidebarMobileToggleComponent
} from "../../../../survey-tool/app/shared/dashboard-side-menu/mobile-toggle/sidebar-mobile-toggle.component";
import { PageContentComponent } from "../../../../survey-tool/app/shared/page-content/page-content.component";
import { InfoCardComponent } from "src/app/shared/reusable-components/info-card/info-card.component";
import { PdfExportService } from "../../services/pdf-export.service";
import { ExploreService } from "../../explore/explore.service";

import { CardConfigComponent } from "../card-config/card-config.component";
import { SectionHiddenNoticeComponent } from "../section-hidden-notice/section-hidden-notice.component";

@Component({
  selector: 'app-open-software',
  templateUrl: './open-software.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    NgOptimizedImage,
    CatalogueUiReusableComponentsModule,
    SidebarMobileToggleComponent,
    PageContentComponent,
    InfoCardComponent,
    CardConfigComponent,
    SectionHiddenNoticeComponent
  ]
})

export class OpenSoftwareComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private exploreService = inject(ExploreService);

  exportActive = false;

  countryCode?: string;
  /** Code used only for the flag/label (EU for the Global default); data still uses countryCode. */
  flagCode?: string;
  countryName?: string;
  surveyAnswers: Object[] = [];
  countrySurveyAnswer?: Object;
  countrySurveyAnswerLastUpdate: string | null = null;
  year?: string;
  lastUpdateDate?: string;

  rfoSoftwarePercentage: (number | null)[] = [null, null];
  rfoSoftwarePercentageDiff: number | null = null;
  rpoSoftwarePercentage: (number | null)[] = [null, null];
  rpoSoftwarePercentageDiff: number | null = null;
  softwareFinancialInvestment: (string | null)[] = [null, null];
  softwareFinancialInvestmentPercentageDiff: number | null = null;
  hasNationalPolicy: string | null = null;
  nationalPolicyClarification: string | null = null;
  policyMandatory: string | null = null;
  hasFinancialStrategy: string | null = null;
  financialStrategyClarification: string | null = null;
  hasMonitoring: string | null = null;
  monitoringClarification: string | null = null;


  constructor(private dataShareService: DataShareService, private pdfService: PdfExportService) {}

  ngOnInit() {
    this.exploreService._lastUpdateDate.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: value => this.lastUpdateDate = value
    });

    this.dataShareService.surveyAnswers.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (answers) => {
        this.surveyAnswers = answers;
        this.initCardValues();
      }
    });

    this.dataShareService.countryName.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (name) => {
        this.countryName = name;
      }
    });

    this.dataShareService.displayCountryCode$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (code) => {
        this.flagCode = code;
      }
    });

    this.dataShareService.year.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (year) => {
        this.year = year;
      }
    })

    this.dataShareService.countryCode.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (code) => {
        this.countryCode = code;
      }
    });

    this.dataShareService.countrySurveyAnswer.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (answer) => {
        this.countrySurveyAnswer = answer;
      }
    });

    this.dataShareService.countrySurveyAnswerMetaData.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (metadata) => {
        this.countrySurveyAnswerLastUpdate = metadata?.lastUpdate ?? null;
      }
    });
  }

  initCardValues() {
    this.rfoSoftwarePercentage[1] = this.dataShareService.calculatePercentage(this.surveyAnswers[1]?.['Policies']?.['Question25']?.['Question25-0'], this.surveyAnswers[1]?.['General']?.['Question3']?.['Question3-0']);
    this.rfoSoftwarePercentage[0] = this.dataShareService.calculatePercentage(this.surveyAnswers[0]?.['Policies']?.['Question25']?.['Question25-0'], this.surveyAnswers[0]?.['General']?.['Question3']?.['Question3-0']);
    this.rfoSoftwarePercentageDiff = this.dataShareService.calculateDiff(this.rfoSoftwarePercentage[0], this.rfoSoftwarePercentage[1]);

    this.rpoSoftwarePercentage[1] = this.dataShareService.calculatePercentage(this.surveyAnswers[1]?.['Policies']?.['Question24']?.['Question24-0'], this.surveyAnswers[1]?.['General']?.['Question2']?.['Question2-0']);
    this.rpoSoftwarePercentage[0] = this.dataShareService.calculatePercentage(this.surveyAnswers[0]?.['Policies']?.['Question24']?.['Question24-0'], this.surveyAnswers[0]?.['General']?.['Question2']?.['Question2-0']);
    this.rpoSoftwarePercentageDiff = this.dataShareService.calculateDiff(this.rpoSoftwarePercentage[0], this.rpoSoftwarePercentage[1]);


    this.softwareFinancialInvestment[1] = this.surveyAnswers[1]?.['Practices']?.['Question72']?.['Question72-0'];
    this.softwareFinancialInvestment[0] = this.surveyAnswers[0]?.['Practices']?.['Question72']?.['Question72-0'];
    this.softwareFinancialInvestmentPercentageDiff = this.dataShareService.calculateDiffAsPercentage(this.softwareFinancialInvestment[0], this.softwareFinancialInvestment[1]);

    this.hasNationalPolicy = this.surveyAnswers[1]?.['Policies']?.['Question22']?.['Question22-0'];
    this.nationalPolicyClarification = this.surveyAnswers[1]?.['Policies']?.['Question22']?.['Question22-3'];
    this.policyMandatory = this.surveyAnswers[1]?.['Policies']?.['Question22-1-0']?.['Question22-1'];

    this.hasFinancialStrategy = this.surveyAnswers[1]?.['Policies']?.['Question23']?.['Question23-0'];
    this.financialStrategyClarification = this.surveyAnswers[1]?.['Policies']?.['Question23']?.['Question23-1'];

    this.hasMonitoring = this.surveyAnswers[1]?.['Practices']?.['Question70']?.['Question70-0'];
    this.monitoringClarification = this.surveyAnswers[1]?.['Practices']?.['Question70']?.['Question70-1'];

  }

  private readonly indicatorsService = inject(CountryPageIndicatorsService);

  /** Left card block renders only if at least one of its cards will be visible (see service). */
  hasAnyLeftCardVisible(): boolean {
    return this.indicatorsService.anyCardVisible([
      { id: '62', hasData: this.rfoSoftwarePercentage[1]       != null },
      { id: '63', hasData: this.softwareFinancialInvestment[1] != null },
      { id: '64', hasData: this.rpoSoftwarePercentage[1]       != null },
    ]);
  }

  hasAnyLeftCardData() {
    return this.dataShareService.hasAnyValue([
      this.rfoSoftwarePercentage[1],
      this.softwareFinancialInvestment[1],
      this.rpoSoftwarePercentage[1],
    ]);
  }

  hasSurveySoftwareData(): boolean {
    const surveyData = this?.countrySurveyAnswer?.['OPEN SCIENCE DIGITAL INFRASTRUCTURE']?.[';Question19']?.['Question19-1'];
    return !!(surveyData && surveyData.trim() !== '');
  }

  exportToPDF(contents: HTMLElement[], filename?: string) {
    this.exportActive = true

    this.pdfService.export(contents, filename).then(() => {
      // this.restoreAnimations(modifiedElements, contents);
      this.exportActive = false;
    }).catch((error) => {
      // this.restoreAnimations(modifiedElements, contents);
      this.exportActive = false;
      console.error('Error during PDF generation:', error);
    });
  }

}
